import { Link } from 'react-router'
import { Table, TableScroll, Td, Th } from '../../ui'
import type { AzureSettings } from '../../lib/api'
import { formatDate } from './LiveFindings'
import { describeFailure, type Load } from './useApi'
import { SettingsSection } from './SettingsSection'
import './live.css'

/**
 * Settings > Azure (Azure CSPM, slice 4): which subscriptions Defenssive can assess, and how to add one.
 *
 * Shown only where Azure assessment is switched on (the server answers 404 otherwise, and the tab is
 * not drawn). Everything here is read: the subscriptions are what Azure listed for Defenssive's
 * application just now, the last assessment is the workspace's own cspm_run row.
 *
 * Azure is part of Monitor (owner, 2026-10-10). Off Monitor the server contacts no one, so this says
 * so and lists nothing, rather than an empty table that would read as "no subscriptions".
 *
 * Reader is the only role asked for (design §3): it sees configuration and never changes it. The
 * optional extra Prowler documents for four App Service checks is deliberately not mentioned.
 */
export function AzureSettingsCard({ azure }: { azure: Load<AzureSettings> }) {
  if (azure.state === 'loading') return <div className="live-empty">Loading…</div>
  if (azure.state === 'error') return <div className="live-empty">{describeFailure(azure.status)}</div>

  const a = azure.data
  if (!a.onMonitor) {
    return (
      <SettingsSection title="Azure">
        <p className="settings-note" style={{ marginTop: 0 }}>
          Azure assessment is part of the Monitor plan. On your current plan Defenssive does not
          contact Azure, and your Microsoft 365 assessment is unchanged.
        </p>
        <p className="settings-note">
          <Link to="/plans">See plans</Link>
        </p>
      </SettingsSection>
    )
  }

  return (
    <>
      <SettingsSection
        title="Azure subscriptions"
        description={
          <p>
            Defenssive assesses each Azure subscription where its application holds the Reader role.
            Reader can see settings, and Microsoft describes it as allowing no changes. Assessments run
            once a week, and again whenever you start one.
          </p>
        }
      >
        <SubscriptionList azure={a} />
        <LastAssessment azure={a} />
      </SettingsSection>

      <SettingsSection
        title="Add a subscription"
        description={
          <p>
            You need to be an Owner or User Access Administrator of the subscription to do this.
            Defenssive asks for no other Azure role. A new assignment can take a few minutes to appear
            here.
          </p>
        }
      >
        <ol className="settings-steps">
          <li>
            In the Azure portal, <span className="mono">portal.azure.com</span>, open{' '}
            <strong>Subscriptions</strong> and select the subscription.
          </li>
          <li>
            Open <strong>Access control (IAM)</strong>, then <strong>Add</strong> &gt;{' '}
            <strong>Add role assignment</strong>.
          </li>
          <li>
            Select <strong>Reader</strong>, then <strong>Next</strong>.
          </li>
          <li>
            Choose <strong>Select members</strong>, search for <strong>Defenssive</strong>, and add
            the application, not a person.
          </li>
          <li>
            Choose <strong>Review + assign</strong>. Repeat for each subscription you want assessed.
          </li>
        </ol>
      </SettingsSection>
    </>
  )
}

function SubscriptionList({ azure }: { azure: AzureSettings }) {
  if (azure.subscriptions === null) {
    return (
      <p className="settings-note">
        Azure did not list your subscriptions just now{azure.readFailure ? ` (${azure.readFailure})` : ''}. If
        you have only just consented to Defenssive, try again in a few minutes.
      </p>
    )
  }
  if (azure.subscriptions.length === 0) {
    return (
      <p className="settings-note">
        No subscription gives Defenssive the Reader role yet, so Azure is not assessed. The steps
        below add one.
      </p>
    )
  }
  return (
    <TableScroll label="Azure subscriptions">
      <Table>
        <thead>
          <tr>
            <Th>Subscription</Th>
            <Th>ID</Th>
            <Th>Assessed</Th>
          </tr>
        </thead>
        <tbody>
          {azure.subscriptions.map((s) => (
            <tr key={s.id}>
              <Td>{s.name}</Td>
              <Td className="mono">{s.id}</Td>
              <Td>{isEnabled(s.state) ? 'Yes' : `No: Azure reports it as ${s.state}`}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  )
}

const isEnabled = (state: string) => state.toLowerCase() === 'enabled'

/** How the last assessment ended, in words; the outcomes are cspm_run's (0193). */
export function describeAzureRun(outcome: string, subscriptionCount: number): string {
  switch (outcome) {
    case 'completed':
      return `Completed, ${subscriptionCount} ${subscriptionCount === 1 ? 'subscription' : 'subscriptions'} assessed.`
    case 'no_subscriptions':
      return 'Nothing to assess: no enabled subscription gave Defenssive the Reader role.'
    case 'no_token':
      return 'Azure refused Defenssive’s sign-in, so nothing was assessed.'
    case 'timed_out':
      return 'It took too long and was stopped, so nothing was assessed.'
    default:
      return 'It did not finish on our side, so nothing was assessed. The next one will try again.'
  }
}

function LastAssessment({ azure }: { azure: AzureSettings }) {
  const last = azure.lastRun
  return (
    <p className="settings-note">
      {last === null
        ? 'Azure has not been assessed yet. The next assessment will include it.'
        : `Last Azure assessment: ${formatDate(last.ranAt)}. ${describeAzureRun(last.outcome, last.subscriptionCount)}`}
    </p>
  )
}
